/**
 * Hook: useEffectivePermissions
 *
 * Fuente única del RBAC dinámico durante la Fase A. Convive con el hook
 * legacy `usePermissions` (matriz estática) hasta que la migración esté
 * completa; NO reemplaza automáticamente al viejo, se opta con feature flag
 * o consumo directo.
 *
 * Retorna el set efectivo de permisos del usuario actual, calculado como:
 *
 *   permisos(user, clubId?) =
 *     ∪ para cada asignación activa (memberRole) del user:
 *         (clubOverride(role, clubId) ?? globalPolicy(role))
 *   ∪ shortcuts implícitos según SystemRole:
 *         admin_union / admin_association  ⇒ TODO (`*`)
 *         club_director (legacy scopeId)    ⇒ política global de "director"
 *
 * `clubId` es opcional pero recomendado: si se pasa, se aplican overrides
 * específicos de ese club. Si es null/undefined, se toma sólo la política
 * global.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuthStore } from "@/stores/authStore";
import {
  combinePolicies,
  getAllPolicies,
} from "@/lib/services/permissionPolicies";
import { getActiveAssignmentsByPerson } from "@/lib/services/memberRoles";
import type {
  MemberRole,
  MemberRoleAssignment,
  RolePermissionPolicy,
} from "@/types";

const WILDCARD = "*";

export interface EffectivePermissionsResult {
  /** true mientras cualquiera de las queries está cargando. */
  loading: boolean;
  /** El super-set de permisos (incluye "*" si aplica). */
  permissions: Set<string>;
  /** Todas las asignaciones activas del usuario. */
  assignments: MemberRoleAssignment[];
  /** Verifica un permiso puntual. */
  can(permissionId: string): boolean;
  /** Verifica que el user tenga TODOS los permisos indicados. */
  canAll(permissionIds: string[]): boolean;
  /** Verifica que el user tenga al menos UNO de los permisos indicados. */
  canAny(permissionIds: string[]): boolean;
  /**
   * Reduce la lista de asignaciones al club indicado. Útil para UIs que
   * muestran "en este club soy: director de aventureros + tesorero de guías".
   */
  assignmentsForClub(clubId: string): MemberRoleAssignment[];
}

/**
 * Deriva el conjunto efectivo de permisos.
 *
 * NOTA: por diseño hacemos todos los reads a Firestore en 2 queries React
 * Query — asignaciones del user y catálogo de políticas — para evitar N+1.
 */
export function useEffectivePermissions(
  clubId?: string,
): EffectivePermissionsResult {
  const profile = useAuthStore((s) => s.profile);

  const { data: assignments = [], isLoading: loadingAssignments } = useQuery({
    queryKey: ["memberRoles", "byPerson", profile?.personId ?? "__none__"],
    queryFn: () =>
      profile?.personId
        ? getActiveAssignmentsByPerson(profile.personId)
        : Promise.resolve([]),
    enabled: !!profile?.personId,
    staleTime: 60_000,
  });

  const { data: policies = [], isLoading: loadingPolicies } = useQuery({
    queryKey: ["rolePolicies", "all"],
    queryFn: () => getAllPolicies(),
    staleTime: 5 * 60_000, // políticas cambian rara vez
  });

  const loading = loadingAssignments || loadingPolicies;

  const permissions = useMemo(() => {
    const acc = new Set<string>();

    // 1) Shortcuts implícitos por SystemRole
    if (
      profile?.primaryRole === "admin_union" ||
      profile?.primaryRole === "admin_association"
    ) {
      acc.add(WILDCARD);
      return acc;
    }

    // Indexar políticas por (role, scope, clubId)
    const globalByRole = new Map<MemberRole, RolePermissionPolicy>();
    const overrideByRoleClub = new Map<string, RolePermissionPolicy>();
    for (const p of policies) {
      if (p.scope === "global") {
        globalByRole.set(p.role, p);
      } else if (p.scope === "club" && p.clubId) {
        overrideByRoleClub.set(`${p.role}__${p.clubId}`, p);
      }
    }

    // 2) Legacy club_director: se comporta como "director" del club scope
    if (profile?.primaryRole === "club_director" && profile?.scopeId) {
      const role: MemberRole = "director";
      const legacyClubId = profile.scopeId;
      const global = globalByRole.get(role);
      const override = clubId
        ? overrideByRoleClub.get(`${role}__${legacyClubId}`)
        : undefined;
      combinePolicies(global, override).forEach((p) => acc.add(p));
    }

    // 3) Multi-cargo: unión de todos los cargos activos
    for (const a of assignments) {
      // Si se pasó clubId, sólo consideramos asignaciones de ESE club para
      // decidir permisos "en el contexto del club". Las asignaciones a otros
      // clubes NO se transfieren.
      if (clubId && a.clubId !== clubId) continue;
      const global = globalByRole.get(a.role);
      const override = overrideByRoleClub.get(`${a.role}__${a.clubId}`);
      combinePolicies(global, override).forEach((p) => acc.add(p));
    }

    return acc;
  }, [profile?.primaryRole, profile?.scopeId, assignments, policies, clubId]);

  const has = (pid: string) =>
    permissions.has(WILDCARD) || permissions.has(pid);

  return {
    loading,
    permissions,
    assignments,
    can: has,
    canAll: (ids) => ids.every(has),
    canAny: (ids) => ids.some(has),
    assignmentsForClub: (id) => assignments.filter((a) => a.clubId === id),
  };
}
